import { debounce } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getReportData, getTotalCount } from '../../../../../api/reports';
import { Loader, Pagination, Table } from '../../../../../common/components';
import { useSetLoading } from '../../../../../common/hooks/appearance';
import { usePagination } from '../../../../../common/hooks/data';
import {
  fetchDashboardFilterData,
  overDueExceptionReportColumns,
  reports,
} from '../../../../../utils/constant';
import * as handlers from '../../../../../utils/handlers';
import DownloadModal from '../../../downloadModal/downloadModal';
import EmailModal from '../../../emailModal/emailModal';
import ItemEmptyTile from '../../../item-empty-tile/item-empty-tile';
import Toolbar from '../../../toolbar/toolbar';
import onDownloadClick from '../../onDownloadClickHandler';
import OverDueExceptionReportRow from './overDueExceptionReportRow';

const OverDueExceptionReport = ({ tableData, fromDate, toDate, totalResults }) => {
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);
  const [overDueTableData, setOverDueTableData] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const [slStartNumber, setSlStartNumber] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [initialRenderDone, setInitialRenderDone] = useState(false);

  useEffect(() => {
    if (!initialRenderDone) {
      setInitialRenderDone(true);
      return;
    }
    getUpdatedTableData('globalFilterChange');
  }, [filterData]);

  useEffect(() => {
    setSlStartNumber(page * limit - limit);
    if (applyFilters) {
      getUpdatedTableData('pagination');
    }
  }, [page, limit, applyFilters]);

  const toggleEmailModal = () => {
    if (overDueTableData?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };

  const toggleDownloadModal = () => {
    if (overDueTableData?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };
  const getUpdatedTableData = async (type = 'pagination', value = '') => {
    setLoading(true);
    setApplyFilters(false);

    let page2 = page;
    let limit2 = limit;
    if (type === 'search' || type === 'globalFilterChange') {
      page2 = 1;
      limit2 = 5;
    }
    try {
      const requestParams = {
        ...fetchDashboardFilterData(filterData),
        startDate: fromDate,
        endDate: toDate,
        timezone: moment.tz.guess(),
        page: page2,
        limit: limit2,
        ...((type === 'search' || searchText.length > 0) && {
          opiOrName: type === 'search' ? value : searchText,
        }),
      };
      const { data } = await getReportData('pastDueReport', requestParams);
      if (type === 'search' || type === 'globalFilterChange') {
        setPage(1);
        setLimit(5);
        const response = await getTotalCount('pastDueReport', {
          ...fetchDashboardFilterData(filterData),
          startDate: fromDate,
          endDate: toDate,
          opiOrName: type === 'search' ? value : searchText,
          timezone: moment.tz.guess(),
        });
        setTotalCount(response?.data?.data?.totalCount);
      }
      setOverDueTableData(data?.data?.items || []);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      handlers.apiErrorHandler(err);
    }
  };

  const debounceApiCall = useCallback(
    debounce((val) => {
      getUpdatedTableData('search', val);
    }, 2000),
    [],
  );
  const onSerchInputChange = (event) => {
    const {
      target: { value },
    } = event;
    setSearchText(value);
    debounceApiCall(value);
  };

  useEffect(() => {
    setOverDueTableData(tableData);
    setTotalCount(totalResults);
    setPage(1);
    setLimit(5);
    setSearchText('');
  }, [tableData, totalResults, setPage, setLimit]);

  const LoaderComponent = () => {
    if (loading) {
      return <Loader />;
    }
    return <ItemEmptyTile />;
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <div className="over-due-exception">
      <Toolbar
        title="Past Due Report"
        optionOneLabel="Email Report"
        optionOneIcon="MdOutlineEmail"
        onOptionOneClick={toggleEmailModal}
        optionTwoLabel="Download Report"
        optionTwoIcon="FiDownload"
        setSearchText={setSearchText}
        onOptionTwoClick={toggleDownloadModal}
        searchText={searchText}
        onSerchInputChange={onSerchInputChange}
        isSearchable
        SearchInputPlaceholder="OPI, Contract Number, Counselor/Manager"
        isTableDataPresent={overDueTableData?.length > 0}
        toolTipText="This report will display the list of missed payments for a given period of time"
      />
      {overDueTableData && overDueTableData?.length > 0 ? (
        <>
          <div className="table-resp-scroll">
            <Table
              headers={overDueExceptionReportColumns}
              bordered
              responsive
              className="dashboard-table equaty-table-rep"
            >
              <tbody>
                {overDueTableData?.map((rowData, rowNumber) => (
                  <OverDueExceptionReportRow
                    key={rowNumber}
                    slNo={slStartNumber + rowNumber + 1}
                    rowData={rowData}
                    id={rowNumber}
                    nestedColumnsNumber={overDueExceptionReportColumns?.length - 3}
                  />
                ))}
              </tbody>
            </Table>
            {loading && <Loader />}
          </div>
          <div className="pagination-araging-report">
            <Pagination
              totalResults={totalCount}
              page={page}
              limit={limit}
              setPage={(pageNo) => {
                setApplyFilters(true);
                setPage(pageNo);
              }}
              setLimit={(rowlimit) => {
                setApplyFilters(true);
                setLimit(rowlimit);
              }}
            />
          </div>
        </>
      ) : (
        <div className="loader-empty">
          <LoaderComponent />
        </div>
      )}

      <EmailModal
        isOpen={isEmailModalOpen}
        toggle={toggleEmailModal}
        reportType={reports.OVER_DUE_EXCEPTION_REPORT}
        apiValues={{
          startDate: fromDate,
          endDate: toDate,
          ...(searchText && { opiOrName: searchText }),
        }}
        newFilters
      />
      <DownloadModal
        isOpen={isDownloadModalOpen}
        toggle={toggleDownloadModal}
        reportType={reports.OVER_DUE_EXCEPTION_REPORT}
        urlParams={{
          startDate: fromDate,
          endDate: toDate,
          action: 'download',
          ...(searchText && { opiOrName: searchText }),
        }}
        onDownloadClick={onDownloadClick}
        willReceiveReportFileInEmail
        newFilters
      />
    </div>
  );
};

OverDueExceptionReport.propTypes = {
  tableData: PropTypes.shape([]),
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  totalResults: PropTypes.number,
};

OverDueExceptionReport.defaultProps = {
  tableData: [],
  fromDate: '',
  toDate: '',
  totalResults: 0,
};

export default OverDueExceptionReport;
